import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type MutableRefObject,
  type MouseEvent as ReactMouseEvent,
  type TouchEvent as ReactTouchEvent,
} from 'react';
import {
  MapContainer,
  TileLayer,
  useMap,
  useMapEvents,
} from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { MapPin, Plus, Trash2, Loader2 } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Input } from '@/components/ui/input';

import type { AreaMapShape } from '@/lib/areas';
import {
  METERS_PER_DEG_LAT,
  metersPerDegLng,
  axisBasisDegrees,
  boundsFromMapPlacementsStrict as boundsFromPlacements,
  squareBoundsMeters,
  isLeafletOverlayOrControlClick,
  applyMapFitBounds,
} from './location-map-shared';
import { AreaPlacementLayer, type AreaPlacement } from './AreaPlacementLayer.tsx';
import { SitePlacementLayer, type SitePlacement } from './SitePlacementLayer';

/**
 * When no area overlays are shown, the map fits a square bounds centered on the site pin (or 0,0)
 * with this side length in meters (max north–south and east–west span of that box).
 */
export const DEFAULT_MAP_VIEW_EXTENT_METERS = 200;

const MIN_DIM_M = 2;
const DEFAULT_WIDTH_M = 40;
const DEFAULT_HEIGHT_M = 25;
const DEFAULT_ORIENTATION = 0;

/** True when server placement matches optimistic fields (query refetch can lag after mutate). */
/** True when the preview patch only adjusts center (not mixed with width/height/orientation/shape). */
function patchIsLiveAreaCenterOnly(patch: Partial<AreaMapPlacement> | undefined): boolean {
  if (!patch || patch.latitude == null || patch.longitude == null) return false;
  const keys: (keyof AreaMapPlacement)[] = [
    'latitude',
    'longitude',
    'widthM',
    'heightM',
    'orientationDegrees',
    'shape',
  ];
  const active = keys.filter((k) => patch[k] != null);
  return active.length === 2 && active.includes('latitude') && active.includes('longitude');
}

function placementMatchesOptimisticPatch(p: AreaMapPlacement, patch: Partial<AreaMapPlacement>): boolean {
  const tolM = 0.75;
  const tolDeg = 0.75;
  const tolLatLng = 1e-5;
  if (patch.widthM != null && Math.abs(patch.widthM - p.widthM) > tolM) return false;
  if (patch.heightM != null && Math.abs(patch.heightM - p.heightM) > tolM) return false;
  if (patch.orientationDegrees != null) {
    const a = patch.orientationDegrees;
    const b = p.orientationDegrees;
    let d = Math.abs(a - b) % 360;
    if (d > 180) d = 360 - d;
    if (d > tolDeg) return false;
  }
  if (patch.latitude != null && Math.abs(patch.latitude - p.latitude) > tolLatLng) return false;
  if (patch.longitude != null && Math.abs(patch.longitude - p.longitude) > tolLatLng) return false;
  return true;
}

const AREA_PALETTE = ['#2563eb', '#16a34a', '#c026d3', '#ea580c', '#0891b2', '#ca8a04'];

/** Filter DevTools console by `LocationPicker` (Verbose level in Chrome). */
function logPicker(phase: string, detail?: Record<string, unknown>) {
  console.debug('[LocationPicker]', phase, detail ?? '');
}

export interface AreaMapPlacement {
  areaId: string;
  name: string;
  latitude: number;
  longitude: number;
  shape: AreaMapShape;
  widthM: number;
  heightM: number;
  orientationDegrees: number;
}

/** Lighting area (`type === 'light'`) eligible for map placement; callers must not include user areas. */
export interface AreaMapCandidate {
  id: string;
  name: string;
}

export type AreaMapPatch = {
  latitude?: number | null;
  longitude?: number | null;
  shape?: AreaMapShape | null;
  widthM?: number | null;
  heightM?: number | null;
  orientationDegrees?: number | null;
};

export interface AreaMapEditorConfig {
  placed: AreaMapPlacement[];
  /** Lighting areas only — no user areas; typically those missing full map placement for the Add picker. */
  candidates: AreaMapCandidate[];
  onPatchArea: (areaId: string, patch: AreaMapPatch) => void | Promise<void>;
  disabled?: boolean;
  isPending?: boolean;
}

interface LocationPickerProps {
  latitude?: number;
  longitude?: number;
  onLocationChange?: (lat: number, lng: number) => void;
  areaMapEditor?: AreaMapEditorConfig;
  className?: string;
  /** Tailwind height class for the map (default h-64). */
  mapHeightClassName?: string;
}

function metersDeltaFromCenter(
  cornerLat: number,
  cornerLng: number,
  centerLat: number,
  centerLng: number,
): { de: number; dn: number } {
  const dLat = cornerLat - centerLat;
  const dLng = cornerLng - centerLng;
  const dn = dLat * METERS_PER_DEG_LAT;
  const de = dLng * metersPerDegLng(centerLat);
  return { de, dn };
}

function widthMFromHandlePosition(
  centerLat: number,
  centerLng: number,
  handleLat: number,
  handleLng: number,
  orientationDeg: number,
): number {
  const { de, dn } = metersDeltaFromCenter(handleLat, handleLng, centerLat, centerLng);
  const { ue, un } = axisBasisDegrees(orientationDeg);
  const halfW = de * ue + dn * un;
  return Math.max(2 * Math.abs(halfW), MIN_DIM_M);
}

function heightMFromHandlePosition(
  centerLat: number,
  centerLng: number,
  handleLat: number,
  handleLng: number,
  orientationDeg: number,
): number {
  const { de, dn } = metersDeltaFromCenter(handleLat, handleLng, centerLat, centerLng);
  const { ve, vn } = axisBasisDegrees(orientationDeg);
  const halfH = de * ve + dn * vn;
  return Math.max(2 * Math.abs(halfH), MIN_DIM_M);
}

/** Bearing of vector from centre to handle, clockwise from north (0..360), then −90° for width-axis orientation. */
function orientationDegreesFromHandleRay(
  centerLat: number,
  centerLng: number,
  handleLat: number,
  handleLng: number,
): number {
  const { de, dn } = metersDeltaFromCenter(handleLat, handleLng, centerLat, centerLng);
  let deg = (Math.atan2(de, dn) * 180) / Math.PI;
  if (deg < 0) deg += 360;
  let o = deg - 90;
  if (o < 0) o += 360;
  if (o >= 360) o -= 360;
  return o;
}

function areaColor(index: number): string {
  return AREA_PALETTE[index % AREA_PALETTE.length];
}

function parseSiteCoords(lat?: number, lng?: number): { lat: number; lng: number } | undefined {
  if (
    lat === undefined ||
    lng === undefined ||
    Number.isNaN(lat) ||
    Number.isNaN(lng) ||
    lat < -90 ||
    lat > 90 ||
    lng < -180 ||
    lng > 180
  ) {
    return undefined;
  }
  return { lat, lng };
}

function MapFitBounds({
  placed,
  siteLat,
  siteLng,
  extentMeters,
  suspendFitRef,
}: {
  placed: AreaMapPlacement[];
  siteLat?: number;
  siteLng?: number;
  extentMeters: number;
  /** When `.current` is true, skip fitBounds (set synchronously on handle dragstart; no React state). */
  suspendFitRef: MutableRefObject<boolean>;
}) {
  const map = useMap();
  const placedRef = useRef(placed);
  placedRef.current = placed;
  const suspendSkipLogRef = useRef(0);

  const placedSig = useMemo(
    () =>
      JSON.stringify(
        placed.map((p) => ({
          id: p.areaId,
          lat: p.latitude,
          lng: p.longitude,
          shape: p.shape,
          w: p.widthM,
          h: p.heightM,
          o: p.orientationDegrees,
        })),
      ),
    [placed],
  );

  useEffect(() => {
    let cancelled = false;
    let rafOuter = 0;
    let rafInner = 0;
    let layoutIdleTimer = 0;

    const apply = () => {
      if (cancelled) return;
      if (suspendFitRef.current) {
        suspendSkipLogRef.current += 1;
        const n = suspendSkipLogRef.current;
        if (n <= 15 || n % 40 === 0) {
          logPicker('MapFitBounds.apply skipped (suspendFitRef)', {
            skipN: n,
            placedCount: placedRef.current.length,
          });
        }
        return;
      }
      suspendSkipLogRef.current = 0;

      const el = map.getContainer();
      if (!el || !el.isConnected) return;
      if (el.clientWidth === 0 || el.clientHeight === 0) return;

      const sz = map.getSize();
      if (!sz || sz.x < 2 || sz.y < 2) return;

      const list = placedRef.current;

      try {
        if (list.length > 0) {
          const b = boundsFromPlacements(list);
          if (!b.isValid()) {
            logPicker('MapFitBounds skip invalid placement bounds', { placedCount: list.length });
            return;
          }
          const site = parseSiteCoords(siteLat, siteLng);
          if (site) {
            b.extend(L.latLng(site.lat, site.lng));
          }
          applyMapFitBounds(map, b, { padding: [28, 28], maxZoom: 18 });
          logPicker('MapFitBounds fit areas + site', { placedCount: list.length });
        } else {
          const site = parseSiteCoords(siteLat, siteLng);
          const lat0 = site?.lat ?? 0;
          const lng0 = site?.lng ?? 0;
          const bounds = squareBoundsMeters(lat0, lng0, extentMeters);
          if (!bounds.isValid()) {
            logPicker('MapFitBounds skip invalid default bounds', { lat0, lng0, extentMeters });
            return;
          }
          applyMapFitBounds(map, bounds, { padding: [20, 20], maxZoom: 18 });
          logPicker('MapFitBounds default extent (no areas)', {
            extentMeters,
            center: [lat0, lng0],
          });
        }
      } catch (e) {
        logPicker('MapFitBounds error', { err: e instanceof Error ? e.message : String(e) });
        try {
          const list2 = placedRef.current;
          const site = parseSiteCoords(siteLat, siteLng);
          const b = list2.length
            ? boundsFromPlacements(list2)
            : squareBoundsMeters(site?.lat ?? 0, site?.lng ?? 0, extentMeters);
          if (b.isValid()) {
            const c = b.getCenter();
            map.setView(c, Math.min(18, Math.max(map.getMinZoom(), map.getZoom() || 12)), { animate: false });
            logPicker('MapFitBounds fallback setView(center)', { lat: c.lat, lng: c.lng });
          }
        } catch (e2) {
          logPicker('MapFitBounds fallback error', { err: e2 instanceof Error ? e2.message : String(e2) });
        }
      }
    };

    const scheduleApply = () => {
      if (cancelled) return;
      rafOuter = requestAnimationFrame(() => {
        if (cancelled) return;
        rafInner = requestAnimationFrame(() => {
          if (!cancelled) apply();
        });
      });
    };

    map.whenReady(() => {
      if (cancelled) return;
      layoutIdleTimer = window.setTimeout(() => {
        layoutIdleTimer = 0;
        if (!cancelled) scheduleApply();
      }, 32);
    });

    return () => {
      cancelled = true;
      if (layoutIdleTimer) window.clearTimeout(layoutIdleTimer);
      cancelAnimationFrame(rafOuter);
      cancelAnimationFrame(rafInner);
    };
  }, [map, placedSig, siteLat, siteLng, extentMeters, suspendFitRef]);

  return null;
}

function MapBackgroundDeselect({
  enabled,
  onDeselect,
}: {
  enabled: boolean;
  onDeselect: () => void;
}) {
  useMapEvents({
    click: (e) => {
      if (!enabled) return;
      if (isLeafletOverlayOrControlClick(e)) return;
      onDeselect();
    },
  });
  return null;
}

export default function LocationPicker({
  latitude,
  longitude,
  onLocationChange,
  areaMapEditor,
  className,
  mapHeightClassName = 'h-64',
}: LocationPickerProps) {
  const reactId = useId();
  const idPrefix = useMemo(() => `location-picker-${reactId.replace(/:/g, '')}`, [reactId]);

  const defaultZoom = 2;

  const center = useMemo((): [number, number] => {
    if (latitude !== undefined && longitude !== undefined && !Number.isNaN(latitude) && !Number.isNaN(longitude)) {
      return [latitude, longitude];
    }
    return [0, 0];
  }, [latitude, longitude]);
  const zoom =
    latitude !== undefined && longitude !== undefined && !Number.isNaN(latitude) && !Number.isNaN(longitude)
      ? 13
      : defaultZoom;

  const editor = areaMapEditor;
  const editorActive = !!editor && !editor.disabled;
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [addCandidateId, setAddCandidateId] = useState<string>('');
  const [dragPreview, setDragPreview] = useState<Record<string, Partial<AreaMapPlacement>> | null>(null);
  /** Synchronous only — never setState in marker dragstart or react-leaflet resets position and kills the drag. */
  const suspendFitDuringHandleDragRef = useRef(false);
  /** Debug: monotonic seq + per-handle move counts (filter console: `[LocationPicker]`). */
  const dragHandleDbgRef = useRef({ seq: 0, widthMove: 0, heightMove: 0, orientMove: 0 });
  const selectedIdRef = useRef<string | null>(null);
  selectedIdRef.current = selectedId;
  const editorPlacedRef = useRef<AreaMapPlacement[]>(editor?.placed ?? []);
  editorPlacedRef.current = editor?.placed ?? [];

  const editorSigRef = useRef<string>('');
  useEffect(() => {
    if (!editor) {
      if (editorSigRef.current !== '') {
        editorSigRef.current = '';
        logPicker('areaMapEditor removed');
      }
      return;
    }
    const sig = JSON.stringify({
      d: editor.disabled,
      p: editor.isPending,
      placed: editor.placed.map((x) => ({
        id: x.areaId,
        lat: x.latitude,
        lng: x.longitude,
        shape: x.shape,
        w: x.widthM,
        h: x.heightM,
        o: x.orientationDegrees,
      })),
      candidates: editor.candidates.map((c) => c.id),
    });
    if (sig === editorSigRef.current) return;
    editorSigRef.current = sig;
    logPicker('areaMapEditor props changed', {
      disabled: editor.disabled,
      isPending: editor.isPending,
      placedCount: editor.placed.length,
      candidateCount: editor.candidates.length,
      placed: editor.placed,
    });
  }, [editor]);

  useEffect(() => {
    if (!editorActive) {
      suspendFitDuringHandleDragRef.current = false;
    }
  }, [editorActive]);

  const placedDisplay = useMemo(() => {
    if (!editor?.placed.length) return [];
    return editor.placed.map((p) => {
      const o = dragPreview?.[p.areaId];
      return o ? { ...p, ...o } : p;
    });
  }, [editor?.placed, dragPreview]);

  const committedSelectedPlacement = useMemo((): AreaMapPlacement | null => {
    if (!editor?.placed.length || !selectedId) return null;
    return editor.placed.find((x) => x.areaId === selectedId) ?? null;
  }, [editor?.placed, selectedId]);

  const resizeHandlesPaletteIndex = useMemo(() => {
    if (!editor?.placed.length || !selectedId) return 0;
    const i = editor.placed.findIndex((x) => x.areaId === selectedId);
    return i >= 0 ? i : 0;
  }, [editor?.placed, selectedId]);

  const centerMarkerAreasForLayer = useMemo(
    () =>
      (editor?.placed ?? []).map(
        (p): AreaPlacement => ({
          id: p.areaId,
          name: p.name,
          latitude: p.latitude,
          longitude: p.longitude,
          shape: p.shape,
          widthM: p.widthM,
          heightM: p.heightM,
          orientationDegrees: p.orientationDegrees,
        }),
      ),
    [editor?.placed],
  );

  const committedHandlesPlacement = useMemo((): AreaPlacement | null => {
    if (!committedSelectedPlacement) return null;
    return {
      id: committedSelectedPlacement.areaId,
      name: committedSelectedPlacement.name,
      latitude: committedSelectedPlacement.latitude,
      longitude: committedSelectedPlacement.longitude,
      shape: committedSelectedPlacement.shape,
      widthM: committedSelectedPlacement.widthM,
      heightM: committedSelectedPlacement.heightM,
      orientationDegrees: committedSelectedPlacement.orientationDegrees,
    };
  }, [committedSelectedPlacement]);

  const sitePinSites = useMemo((): SitePlacement[] => {
    if (latitude === undefined || longitude === undefined || Number.isNaN(latitude) || Number.isNaN(longitude)) {
      return [];
    }
    return [{ id: 'site', name: 'Site', latitude, longitude }];
  }, [latitude, longitude]);

  const placedDisplayRef = useRef(placedDisplay);
  placedDisplayRef.current = placedDisplay;

  /** Drop optimistic map fields once `editor.placed` matches (avoids React-Leaflet snapping the handle to stale props before refetch). */
  useEffect(() => {
    if (!editor?.placed.length) return;
    setDragPreview((prev) => {
      if (!prev) return prev;
      const next: Record<string, Partial<AreaMapPlacement>> = { ...prev };
      let changed = false;
      for (const areaId of Object.keys(next)) {
        const patch = next[areaId];
        const p = editor.placed.find((x) => x.areaId === areaId);
        if (!p) {
          delete next[areaId];
          changed = true;
          continue;
        }
        if (placementMatchesOptimisticPatch(p, patch)) {
          delete next[areaId];
          changed = true;
        }
      }
      if (!changed) return prev;
      const ids = Object.keys(next);
      return ids.length ? next : null;
    });
  }, [editor?.placed]);

  const selectedPlacement = useMemo(
    () => placedDisplay.find((p) => p.areaId === selectedId) ?? null,
    [placedDisplay, selectedId],
  );

  /** Live center for handle sync — only when preview is center-only (width/orient drags must not inherit lat/lng). */
  const selectedLiveCenterLatLng = useMemo((): { lat: number; lng: number } | null => {
    if (!selectedId || !dragPreview) return null;
    const d = dragPreview[selectedId];
    if (!patchIsLiveAreaCenterOnly(d)) return null;
    return { lat: d.latitude!, lng: d.longitude! };
  }, [dragPreview, selectedId]);

  const openAddDialog = useCallback(() => {
    const first = editor?.candidates[0]?.id ?? '';
    logPicker('open add placement dialog', { candidateCount: editor?.candidates.length ?? 0, defaultId: first });
    setAddCandidateId(first);
    setAddOpen(true);
  }, [editor?.candidates]);

  const handleConfirmAdd = useCallback(async () => {
    if (!editor || !addCandidateId) return;
    const lat0 =
      latitude !== undefined && longitude !== undefined && !Number.isNaN(latitude) && !Number.isNaN(longitude)
        ? latitude
        : center[0];
    const lng0 =
      latitude !== undefined && longitude !== undefined && !Number.isNaN(latitude) && !Number.isNaN(longitude)
        ? longitude
        : center[1];
    logPicker('add placement confirm → onPatchArea', {
      areaId: addCandidateId,
      lat0,
      lng0,
      usedSitePin: Boolean(
        latitude !== undefined &&
          longitude !== undefined &&
          !Number.isNaN(latitude) &&
          !Number.isNaN(longitude),
      ),
    });
    try {
      await editor.onPatchArea(addCandidateId, {
        latitude: lat0,
        longitude: lng0,
        shape: 'rectangle',
        widthM: DEFAULT_WIDTH_M,
        heightM: DEFAULT_HEIGHT_M,
        orientationDegrees: DEFAULT_ORIENTATION,
      });
      logPicker('add placement onPatchArea resolved', { areaId: addCandidateId });
      setSelectedId(addCandidateId);
      setAddOpen(false);
    } catch (err) {
      logPicker('add placement onPatchArea rejected', {
        areaId: addCandidateId,
        err: err instanceof Error ? err.message : String(err),
      });
    }
  }, [addCandidateId, center, editor, latitude, longitude]);

  const clearPlacement = useCallback(async () => {
    if (!editor || !selectedId) return;
    logPicker('clear placement → onPatchArea (all map fields null)', { areaId: selectedId });
    try {
      await editor.onPatchArea(selectedId, {
        latitude: null,
        longitude: null,
        shape: null,
        widthM: null,
        heightM: null,
        orientationDegrees: null,
      });
      logPicker('clear placement resolved', { areaId: selectedId });
      setSelectedId(null);
    } catch (err) {
      logPicker('clear placement rejected', {
        areaId: selectedId,
        err: err instanceof Error ? err.message : String(err),
      });
    }
  }, [editor, selectedId]);

  const patchSelected = useCallback(
    async (patch: AreaMapPatch) => {
      if (!editor || !selectedId) return;
      logPicker('patchSelected → onPatchArea', { areaId: selectedId, patch });
      await editor.onPatchArea(selectedId, patch);
    },
    [editor, selectedId],
  );

  const onSelectedShapeChange = useCallback(
    (value: string) => {
      logPicker('shape Select change', { shape: value });
      void patchSelected({ shape: value as AreaMapShape });
    },
    [patchSelected],
  );

  const onAddDialogCandidateChange = useCallback((value: string) => {
    logPicker('add dialog candidate changed', { areaId: value });
    setAddCandidateId(value);
  }, []);

  const onAddDialogClose = useCallback(() => {
    setAddOpen(false);
  }, []);

  const onAddDialogConfirmClick = useCallback(() => {
    void handleConfirmAdd();
  }, [handleConfirmAdd]);

  const onSiteMarkerDragEnd = useCallback(
    (lat: number, lng: number) => {
      logPicker('site marker dragend', { lat, lng });
      onLocationChange?.(lat, lng);
    },
    [onLocationChange],
  );

  const onCenterDrag = useCallback((areaId: string, lat: number, lng: number) => {
    setDragPreview((prev) => ({
      ...(prev ?? {}),
      [areaId]: { ...(prev?.[areaId] ?? {}), latitude: lat, longitude: lng },
    }));
  }, []);

  const onCenterDragEnd = useCallback(
    async (areaId: string, lat: number, lng: number) => {
      if (!editor) return;
      logPicker('area center marker dragend → onPatchArea', { areaId, lat, lng });
      suspendFitDuringHandleDragRef.current = false;
      setDragPreview((prev) => ({
        ...(prev ?? {}),
        [areaId]: { ...(prev?.[areaId] ?? {}), latitude: lat, longitude: lng },
      }));
      try {
        await editor.onPatchArea(areaId, { latitude: lat, longitude: lng });
      } catch (err) {
        setDragPreview((prev) => {
          if (!prev) return null;
          const next = { ...prev };
          delete next[areaId];
          return Object.keys(next).length ? next : null;
        });
        throw err;
      }
    },
    [editor],
  );

  const onWidthHandleDrag = useCallback((areaId: string, lat: number, lng: number) => {
    const p = placedDisplayRef.current.find((x) => x.areaId === areaId);
    if (!p) {
      logPicker('onWidthHandleDrag bail (no placement)', { areaId, lat, lng });
      return;
    }
    const widthM = heightMFromHandlePosition(p.latitude, p.longitude, lat, lng, p.orientationDegrees);
    dragHandleDbgRef.current.widthMove += 1;
    const n = dragHandleDbgRef.current.widthMove;
    if (n <= 12 || n % 30 === 0) {
      logPicker('onWidthHandleDrag', { moveN: n, areaId, lat, lng, widthM, center: [p.latitude, p.longitude] });
    }
    setDragPreview((prev) => ({
      ...(prev ?? {}),
      [areaId]: { ...(prev?.[areaId] ?? {}), widthM },
    }));
  }, []);

  const onWidthHandleDragEnd = useCallback(
    async (areaId: string, lat: number, lng: number) => {
      if (!editor) return;
      const p = placedDisplayRef.current.find((x) => x.areaId === areaId);
      if (!p) return;
      const widthM = heightMFromHandlePosition(p.latitude, p.longitude, lat, lng, p.orientationDegrees);
      logPicker('width handle dragend → onPatchArea', {
        areaId,
        widthM,
        movesDuringGesture: dragHandleDbgRef.current.widthMove,
      });
      suspendFitDuringHandleDragRef.current = false;
      setDragPreview((prev) => ({
        ...(prev ?? {}),
        [areaId]: { ...(prev?.[areaId] ?? {}), widthM },
      }));
      try {
        await editor.onPatchArea(areaId, { widthM });
      } catch (err) {
        setDragPreview((prev) => {
          if (!prev) return null;
          const next = { ...prev };
          delete next[areaId];
          return Object.keys(next).length ? next : null;
        });
        throw err;
      }
    },
    [editor],
  );

  const onHeightHandleDrag = useCallback((areaId: string, lat: number, lng: number) => {
    const p = placedDisplayRef.current.find((x) => x.areaId === areaId);
    if (!p) {
      logPicker('onHeightHandleDrag bail (no placement)', { areaId, lat, lng });
      return;
    }
    const heightM = widthMFromHandlePosition(p.latitude, p.longitude, lat, lng, p.orientationDegrees);
    dragHandleDbgRef.current.heightMove += 1;
    const n = dragHandleDbgRef.current.heightMove;
    if (n <= 12 || n % 30 === 0) {
      logPicker('onHeightHandleDrag', { moveN: n, areaId, lat, lng, heightM, center: [p.latitude, p.longitude] });
    }
    setDragPreview((prev) => ({
      ...(prev ?? {}),
      [areaId]: { ...(prev?.[areaId] ?? {}), heightM },
    }));
  }, []);

  const onHeightHandleDragEnd = useCallback(
    async (areaId: string, lat: number, lng: number) => {
      if (!editor) return;
      const p = placedDisplayRef.current.find((x) => x.areaId === areaId);
      if (!p) return;
      const heightM = widthMFromHandlePosition(p.latitude, p.longitude, lat, lng, p.orientationDegrees);
      logPicker('height handle dragend → onPatchArea', {
        areaId,
        heightM,
        movesDuringGesture: dragHandleDbgRef.current.heightMove,
      });
      suspendFitDuringHandleDragRef.current = false;
      setDragPreview((prev) => ({
        ...(prev ?? {}),
        [areaId]: { ...(prev?.[areaId] ?? {}), heightM },
      }));
      try {
        await editor.onPatchArea(areaId, { heightM });
      } catch (err) {
        setDragPreview((prev) => {
          if (!prev) return null;
          const next = { ...prev };
          delete next[areaId];
          return Object.keys(next).length ? next : null;
        });
        throw err;
      }
    },
    [editor],
  );

  const onOrientationHandleDrag = useCallback((areaId: string, lat: number, lng: number) => {
    const p = placedDisplayRef.current.find((x) => x.areaId === areaId);
    if (!p) {
      logPicker('onOrientationHandleDrag bail (no placement)', { areaId, lat, lng });
      return;
    }
    const orientationDegrees = orientationDegreesFromHandleRay(p.latitude, p.longitude, lat, lng);
    dragHandleDbgRef.current.orientMove += 1;
    const n = dragHandleDbgRef.current.orientMove;
    if (n <= 12 || n % 30 === 0) {
      logPicker('onOrientationHandleDrag', { moveN: n, areaId, lat, lng, orientationDegrees });
    }
    setDragPreview((prev) => ({
      ...(prev ?? {}),
      [areaId]: { ...(prev?.[areaId] ?? {}), orientationDegrees },
    }));
  }, []);

  const onOrientationHandleDragEnd = useCallback(
    async (areaId: string, lat: number, lng: number) => {
      if (!editor) return;
      const p = placedDisplayRef.current.find((x) => x.areaId === areaId);
      if (!p) return;
      const orientationDegrees = orientationDegreesFromHandleRay(p.latitude, p.longitude, lat, lng);
      logPicker('orientation handle dragend → onPatchArea', {
        areaId,
        orientationDegrees,
        movesDuringGesture: dragHandleDbgRef.current.orientMove,
      });
      suspendFitDuringHandleDragRef.current = false;
      setDragPreview((prev) => ({
        ...(prev ?? {}),
        [areaId]: { ...(prev?.[areaId] ?? {}), orientationDegrees },
      }));
      try {
        await editor.onPatchArea(areaId, { orientationDegrees });
      } catch (err) {
        setDragPreview((prev) => {
          if (!prev) return null;
          const next = { ...prev };
          delete next[areaId];
          return Object.keys(next).length ? next : null;
        });
        throw err;
      }
    },
    [editor],
  );

  const dimDebounceRef = useRef<ReturnType<typeof setTimeout>>();
  const queueDimensionPatch = useCallback(
    (patch: AreaMapPatch) => {
      if (!editor) return;
      if (dimDebounceRef.current) clearTimeout(dimDebounceRef.current);
      logPicker('queueDimensionPatch (debounced 280ms)', { patch, selectedId: selectedIdRef.current });
      dimDebounceRef.current = setTimeout(() => {
        const id = selectedIdRef.current;
        if (!id) return;
        logPicker('queueDimensionPatch fire → onPatchArea', { areaId: id, patch });
        void editor.onPatchArea(id, patch);
      }, 280);
    },
    [editor],
  );

  const onOrientationDegreesInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const n = parseFloat(e.target.value);
      if (Number.isNaN(n)) return;
      const clamped = Math.min(359.99, Math.max(0, n));
      queueDimensionPatch({ orientationDegrees: clamped });
    },
    [queueDimensionPatch],
  );

  const onOrientationDegreesInputBlur = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (dimDebounceRef.current) {
        clearTimeout(dimDebounceRef.current);
        dimDebounceRef.current = undefined;
      }
      const n = parseFloat(e.target.value);
      if (!Number.isNaN(n) && selectedId) {
        const clamped = Math.min(359.99, Math.max(0, n));
        void patchSelected({ orientationDegrees: clamped });
      }
    },
    [patchSelected, selectedId],
  );

  const onWidthRangeInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      queueDimensionPatch({ widthM: Number(e.target.value) });
    },
    [queueDimensionPatch],
  );

  const onWidthRangeCommit = useCallback(
    (e: ReactMouseEvent<HTMLInputElement> | ReactTouchEvent<HTMLInputElement>) => {
      if (dimDebounceRef.current) {
        clearTimeout(dimDebounceRef.current);
        dimDebounceRef.current = undefined;
      }
      const v = Number(e.currentTarget.value);
      void patchSelected({ widthM: v });
    },
    [patchSelected],
  );

  const onHeightRangeInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      queueDimensionPatch({ heightM: Number(e.target.value) });
    },
    [queueDimensionPatch],
  );

  const onHeightRangeCommit = useCallback(
    (e: ReactMouseEvent<HTMLInputElement> | ReactTouchEvent<HTMLInputElement>) => {
      if (dimDebounceRef.current) {
        clearTimeout(dimDebounceRef.current);
        dimDebounceRef.current = undefined;
      }
      const v = Number(e.currentTarget.value);
      void patchSelected({ heightM: v });
    },
    [patchSelected],
  );

  useEffect(
    () => () => {
      if (dimDebounceRef.current) clearTimeout(dimDebounceRef.current);
    },
    [],
  );

  useEffect(() => {
    if (dimDebounceRef.current) {
      clearTimeout(dimDebounceRef.current);
      dimDebounceRef.current = undefined;
    }
    logPicker('selected area changed', { selectedId });
  }, [selectedId]);

  return (
    <div className={cn('w-full space-y-2', className)}>
      {editorActive && (
        <div className="flex flex-wrap items-center gap-2">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-1"
                onClick={openAddDialog}
                disabled={!editor.candidates.length || editor.isPending}
              >
                <Plus className="h-4 w-4" />
                Add area map
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-xs">
              Choose an existing area and set default map placement. Does not create a new area.
            </TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-1"
                onClick={clearPlacement}
                disabled={!selectedId || editor.isPending}
              >
                {editor.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4" />
                )}
                Clear placement
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-xs">
              Removes map coordinates and shape for the selected area only.
            </TooltipContent>
          </Tooltip>
          {selectedPlacement && (
            <span className="text-xs text-muted-foreground">
              Selected: <span className="font-medium text-foreground">{selectedPlacement.name}</span>
            </span>
          )}
        </div>
      )}

      <div className={cn('w-full rounded-md overflow-hidden border border-border relative z-0', mapHeightClassName)}>
        <div
          id={`${idPrefix}-help-anchor`}
          className="absolute right-1 top-1 z-[1000] bg-primary/95 rounded-lg pointer-events-auto"
        >
          <Dialog>
            <Tooltip>
              <TooltipTrigger asChild>
                <DialogTrigger asChild>
                  <Button
                    id={`${idPrefix}-help-button`}
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 rounded-lg"
                    aria-label="Help"
                  >
                    ?
                  </Button>
                </DialogTrigger>
              </TooltipTrigger>
              <TooltipContent
                side="bottom"
                id={`${idPrefix}-help-tooltip`}
                className="bg-primary/95 text-primary-foreground shadow-lg"
              >
                <span className="font-medium">Click</span> for help
              </TooltipContent>
            </Tooltip>
            <DialogContent id={`${idPrefix}-help-dialog`} className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle id={`${idPrefix}-help-dialog-title`}>Location picker</DialogTitle>
                <DialogDescription id={`${idPrefix}-help-dialog-desc`}>
                  Pan and zoom the map, then adjust the site pin and (when enabled) area overlays.
                </DialogDescription>
              </DialogHeader>

              <div id={`${idPrefix}-help-dialog-body`} className="space-y-3 text-sm">
                <ul className="list-disc space-y-1 pl-5">
                  {onLocationChange ? (
                    <li>
                      <span className="font-bold">Site location:</span> drag the blue site pin to move the site.
                    </li>
                  ) : (
                    <li>
                      <span className="font-bold">Site location:</span> shown as a pin on the map (read-only in this
                      view).
                    </li>
                  )}
                  <li>
                    <span className="font-bold">Zoom:</span> use the mouse wheel / trackpad over the map.
                  </li>
                  <li>
                    <span className="font-bold">Deselect:</span> click empty map background to clear the selected area
                    selection.
                  </li>
                  {editorActive ? (
                    <>
                      <li>
                        <span className="font-bold">Select area:</span> click an area overlay to select it (handles
                        appear when editable).
                      </li>
                      <li>
                        <span className="font-bold">Move area:</span> drag the center marker on the selected overlay.
                      </li>
                      <li>
                        <span className="font-bold">Resize / rotate:</span> drag the wide bar (width), tall bar
                        (height), or triangle (rotation) handles.
                      </li>
                      <li>
                        <span className="font-bold">Add placement:</span> use <span className="font-bold">Add area map</span>{' '}
                        to place an area that does not have map fields yet.
                      </li>
                      <li>
                        <span className="font-bold">Clear placement:</span> removes map fields for the{' '}
                        <span className="font-bold">selected</span> area only.
                      </li>
                    </>
                  ) : (
                    <li>
                      <span className="font-bold">Area overlays:</span> when editing is enabled for your role, you can
                      place and adjust overlays; otherwise overlays are shown read-only.
                    </li>
                  )}
                </ul>
              </div>
            </DialogContent>
          </Dialog>
        </div>
        <MapContainer
          center={center}
          zoom={zoom}
          style={{ height: '100%', width: '100%', zIndex: 0 }}
          scrollWheelZoom
          zoomAnimation={false}
          fadeAnimation={false}
          markerZoomAnimation={false}
        >
          <MapBackgroundDeselect
            enabled={true}
            onDeselect={() => {
              setSelectedId(null);
              // Drop any browser focus state on the site pin (and anything else) when clicking blank map.
              if (document?.activeElement instanceof HTMLElement) document.activeElement.blur();
            }}
          />
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <AreaPlacementLayer
            areas={placedDisplay.map(
              (p): AreaPlacement => ({
                id: p.areaId,
                name: p.name,
                latitude: p.latitude,
                longitude: p.longitude,
                shape: p.shape,
                widthM: p.widthM,
                heightM: p.heightM,
                orientationDegrees: p.orientationDegrees,
              }),
            )}
            centerMarkerAreas={centerMarkerAreasForLayer}
            selectedAreaId={selectedId}
            editable={editorActive}
            onSelectArea={(id) => {
              logPicker('area overlay click', { areaId: id, interactive: editorActive });
              if (!editorActive) return;
              setSelectedId(id);
            }}
            showCenterMarkers={editorActive}
            onCenterDrag={onCenterDrag}
            onCenterDragEnd={onCenterDragEnd}
            selectedCommittedForHandles={committedHandlesPlacement}
            selectedLiveCenterForHandles={selectedLiveCenterLatLng}
            selectedIconOrientationDegreesForHandles={selectedPlacement?.orientationDegrees ?? 0}
            handlesColor={areaColor(resizeHandlesPaletteIndex)}
            suspendFitDuringHandleDragRef={suspendFitDuringHandleDragRef}
            onHeightHandleDrag={onHeightHandleDrag}
            onHeightHandleDragEnd={onHeightHandleDragEnd}
            onWidthHandleDrag={onWidthHandleDrag}
            onWidthHandleDragEnd={onWidthHandleDragEnd}
            onOrientationHandleDrag={onOrientationHandleDrag}
            onOrientationHandleDragEnd={onOrientationHandleDragEnd}
          />
          {sitePinSites.length ? (
            <SitePlacementLayer
              mode="single-pin"
              markerStyle="pin"
              sites={sitePinSites}
              selectedSiteId={null}
              draggable={Boolean(onLocationChange)}
              behindOverlaysWhen={Boolean(selectedId)}
              suspendFitRef={suspendFitDuringHandleDragRef}
              onDragEnd={(_id, lat, lng) => onSiteMarkerDragEnd(lat, lng)}
            />
          ) : null}
          <MapFitBounds
            placed={placedDisplay}
            siteLat={latitude}
            siteLng={longitude}
            extentMeters={DEFAULT_MAP_VIEW_EXTENT_METERS}
            suspendFitRef={suspendFitDuringHandleDragRef}
          />
        </MapContainer>
      </div>

      {editorActive && selectedPlacement && (
        <div className="rounded-md border border-border bg-muted/30 p-3 space-y-3">
          <div className="flex items-center gap-2 text-sm font-medium text-foreground">
            <MapPin className="h-4 w-4 shrink-0" />
            Edit placement — {selectedPlacement.name}
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label className="text-xs">Shape</Label>
              <Select
                value={selectedPlacement.shape}
                onValueChange={onSelectedShapeChange}
                disabled={editor?.isPending}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="rectangle">Rectangle</SelectItem>
                  <SelectItem value="oval">Oval</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Orientation (°)</Label>
              <Input
                type="number"
                min={0}
                max={359.99}
                step={1}
                value={selectedPlacement.orientationDegrees}
                onChange={onOrientationDegreesInputChange}
                onBlur={onOrientationDegreesInputBlur}
                disabled={editor?.isPending}
              />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <div className="flex justify-between text-xs">
                <span>Width (m)</span>
                <span className="tabular-nums">{Math.round(selectedPlacement.widthM)}</span>
              </div>
              <input
                type="range"
                min={MIN_DIM_M}
                max={500}
                step={1}
                value={Math.min(500, selectedPlacement.widthM)}
                onChange={onWidthRangeInputChange}
                onMouseUp={onWidthRangeCommit}
                onTouchEnd={onWidthRangeCommit}
                disabled={editor?.isPending}
                className="w-full accent-primary"
              />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <div className="flex justify-between text-xs">
                <span>Height (m)</span>
                <span className="tabular-nums">{Math.round(selectedPlacement.heightM)}</span>
              </div>
              <input
                type="range"
                min={MIN_DIM_M}
                max={500}
                step={1}
                value={Math.min(500, selectedPlacement.heightM)}
                onChange={onHeightRangeInputChange}
                onMouseUp={onHeightRangeCommit}
                onTouchEnd={onHeightRangeCommit}
                disabled={editor?.isPending}
                className="w-full accent-primary"
              />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Drag the blue site pin to move the site. Select an area, then drag the center dot to move it. Drag the wide
            bar (width), tall bar (height), or triangle (rotation) handles to adjust shape. Oval uses the same handles
            and shows an ellipse from width and height.
          </p>
        </div>
      )}

      {editorActive && (
        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Add map placement</DialogTitle>
              <DialogDescription>
                Pick an area that does not have map coordinates yet. This only sets optional map fields — it does not
                create a new area.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2 py-2">
              <Label>Area</Label>
              <Select value={addCandidateId} onValueChange={onAddDialogCandidateChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Select area" />
                </SelectTrigger>
                <SelectContent>
                  {editor?.candidates.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button type="button" variant="outline" onClick={onAddDialogClose}>
                Cancel
              </Button>
              <Button type="button" onClick={onAddDialogConfirmClick} disabled={!addCandidateId || editor?.isPending}>
                {editor?.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Add placement'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
